import React, { useState } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import './ToolBar.css';

const ToolBar = ({ onLayoutChange }) => {
  const [activeTools, setActiveTools] = useState(['Wwwc', 'Pan', 'Zoom']);

  const tools = [
    { name: 'Pan', icon: 'fa-arrows', title: 'Pan (Middle Mouse Button)' },
    { name: 'Zoom', icon: 'fa-search-plus', title: 'Zoom (Right Mouse Button)' },
    { name: 'Wwwc', icon: 'fa-sun-o', title: 'Window/Level (Left Mouse Button)' },
    { name: 'Magnify', icon: 'fa-search', title: 'Magnify' },
    { name: 'Length', icon: 'fa-arrows-v', title: 'Length' },
    { name: 'Angle', icon: 'fa-angle-left', title: 'Angle' },
    { name: 'EllipticalRoi', icon: 'fa-circle-o', title: 'Ellipse ROI' },
    { name: 'RectangleRoi', icon: 'fa-square-o', title: 'Rectangle ROI' }
  ];

  const layouts = ['1x1', '1x2', '2x1', '2x2'];

  const handleToolClick = (toolName) => {
    // Only Pan and Zoom are toggleable; they start enabled by default.
    const elements = document.querySelectorAll('.viewport');
    const toggleable = new Set(['Pan', 'Zoom']);

    setActiveTools(prev => {
      const isToggle = toggleable.has(toolName);

      if (isToggle) {
        const isActive = prev.includes(toolName);
        const next = isActive ? prev.filter(t => t !== toolName) : [...prev, toolName];

        elements.forEach(element => {
          try {
            if (!isActive) {
              if (toolName === 'Zoom') {
                cornerstoneTools.setToolActiveForElement(element, 'Zoom', { mouseButtonMask: 2 });
              } else if (toolName === 'Pan') {
                cornerstoneTools.setToolActiveForElement(element, 'Pan', { mouseButtonMask: 4 });
              }
            } else {
              cornerstoneTools.setToolPassiveForElement(element, toolName);
            }
          } catch (e) {
            console.error('Error toggling tool on element:', e);
          }
        });

        return next;
      }

      // Non-toggleable tool clicked: make it the active interaction tool, but preserve Pan/Zoom if they are enabled.
      const preserved = prev.filter(t => toggleable.has(t));

      elements.forEach(element => {
        try {
          // Deactivate other non-toggleable tools
          const prevNonToggle = prev.filter(t => !toggleable.has(t) && t !== toolName);
          prevNonToggle.forEach(t => {
            try { cornerstoneTools.setToolPassiveForElement(element, t); } catch (e) { }
          });

          // Activate the clicked tool with appropriate mapping
          if (toolName === 'Wwwc') {
            cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });
          } else {
            cornerstoneTools.setToolActiveForElement(element, toolName, { mouseButtonMask: 1 });
          }
        } catch (e) {
          console.error('Error setting non-toggleable tool active:', e);
        }
      });

      return [...preserved, toolName];
    });
  };

  const handleReset = () => {
    // Reset all viewports to default state
    const elements = document.querySelectorAll('.viewport');
    elements.forEach(element => {
      try {
        const enabledElement = cornerstone.getEnabledElement(element);
        if (enabledElement) {
          cornerstone.reset(element);
        }
      } catch (e) {
        console.error('Error resetting viewport:', e);
      }
    });
  };

  const handleInvert = () => {
    // Invert all viewport colors
    const elements = document.querySelectorAll('.viewport');
    elements.forEach(element => {
      try {
        const enabledElement = cornerstone.getEnabledElement(element);
        if (enabledElement) {
          const viewport = enabledElement.viewport;
          viewport.invert = !viewport.invert;
          cornerstone.setViewport(element, viewport);
        }
      } catch (e) {
        console.error('Error inverting viewport:', e);
      }
    });
  };

  const handleClearTools = () => {
    // Clear all tool annotations
    const elements = document.querySelectorAll('.viewport');
    elements.forEach(element => {
      try {
        // Clear tool state for each specific tool
        const toolNames = ['Length', 'Angle', 'EllipticalRoi', 'RectangleRoi', 'Probe', 'ArrowAnnotate'];
        toolNames.forEach(toolName => {
          const toolState = cornerstoneTools.getToolState(element, toolName);
          if (toolState && toolState.data) {
            toolState.data = [];
          }
        });

        // Force update the image
        cornerstone.updateImage(element);
      } catch (e) {
        console.error('Error clearing tools:', e);
      }
    });
  };

  return (
    <div className="toolbar">
      {/* Tools Section */}
      <div className="btn-group" role="group">
        {tools.map(tool => (
          <button
            key={tool.name}
            type="button"
            className={`btn btn-sm ${activeTools.includes(tool.name) ? 'btn-primary' : 'btn-default'}`}
            onClick={() => handleToolClick(tool.name)}
            title={tool.title}
          >
            <i className={`fa ${tool.icon}`}></i>
          </button>
        ))}
      </div>

      {/* Layout Selection */}
      <div className="choose-layout">
        <div className="btn-group" role="group">
          <span className="layout-label">
            Layout:
          </span>
          {layouts.map(layout => (
            <button
              key={layout}
              type="button"
              className="btn btn-sm btn-default"
              onClick={() => onLayoutChange(layout)}
            >
              {layout}
            </button>
          ))}
        </div>
      </div>

      {/* Utility Buttons */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={handleReset}
          title="Reset"
        >
          <i className="fa fa-refresh"></i> Reset
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={handleInvert}
          title="Invert"
        >
          <i className="fa fa-adjust"></i> Invert
        </button>
        <button
          type="button"
          className="btn btn-sm btn-default"
          onClick={handleClearTools}
          title="Clear Annotations"
        >
          <i className="fa fa-eraser"></i> Clear
        </button>
      </div>
    </div>
  );
};

export default ToolBar;
